<template>
  <div class="pagination justify-content-center d-flex">
    <li
      class="page-item me-auto"
      :class="{ disabled: !canGoBack }"
      v-if="showPaginationButtons"
    >
      <button
        class="page-link"
        @click="$emit('update:modelValue', currentPage - 1)"
        tabindex=""
        :disabled="!canGoBack"
      >
        <i  class="me-md-2 far fa-arrow-left" ></i>
        <span class="d-none d-md-inline">
          {{ $t('GENERAL.LABEL.PREVIOUS') }}
        </span>
      </button>
    </li>
    <li
      class="page-item"
      @click="$emit('update:modelValue', 1)"
      v-if="showStartSeparator"
    >
      <button class="page-link" is="button" tabindex="1">
        1
      </button>
    </li>
    <li
      class="page-item disabled bv-d-xs-down-none"
      role="separator"
      v-if="showStartSeparator"
    >
      <span class="page-link">
        …
      </span>
    </li>
    <li
      class="page-item"
      v-for="page in pages"
      :key="page"
      :class="{ active: page === currentPage }"
      @click="$emit('update:modelValue', page)"
    >
      <button class="page-link" is="button" :tabindex="page === currentPage ? -1 : 0">
        {{ page }}
      </button>
    </li>
    <li
      class="page-item disabled bv-d-xs-down-none"
      role="separator"
      v-if="showEndSeparator"
    >
      <span class="page-link">
        …
      </span>
    </li>
    <li
      class="page-item"
      @click="$emit('update:modelValue', totalPages)"
      v-if="showEndSeparator"
    >
      <button class="page-link" is="button" tabindex="1">
        {{ totalPages }}
      </button>
    </li>
    <li
      class="page-item ms-auto"
      :class="{ disabled: !canGoForward }"
      v-if="showPaginationButtons"
    >
      <button
        class="page-link"
        @click="$emit('update:modelValue', currentPage + 1)"
        tabindex=""
        :disabled="!canGoForward"
      >
        <span class="d-none d-md-inline">
          {{ $t('GENERAL.LABEL.NEXT') }}
        </span>
        <i  class="ms-md-2 far fa-arrow-right" ></i>
      </button>
    </li>
  </div>
</template>

<script lang="ts" setup>
import { toRef } from "vue";
import { computed } from "vue";

const props = defineProps({
  totalRows: {
    type: Number,
    required: true,
  },
  perPage: {
    type: Number,
    default: 10,
  },
  modelValue: {
    type: Number,
    required: true,
  },
  maxPagesShown: {
    type: Number,
    default: 5,
  },
  showPaginationButtons: {
    type: Boolean,
    default: true,
  },
});

const currentPage = toRef(props, "modelValue");
const totalPages = computed(() => Math.ceil(props.totalRows / props.perPage));
const maxPagesShown = computed(() => Math.min(props.maxPagesShown, totalPages.value));
const pages = computed(() => {
  let start = Math.max(1, currentPage.value - Math.ceil(maxPagesShown.value / 2) + 1);
  const end = Math.min(totalPages.value, start + maxPagesShown.value - 1);
  start = Math.max(1, end - maxPagesShown.value + 1);
  const pages = Array.from({ length: end - start + 1 }, (_, i) => start + i);
  if(pages[0] > 1) {
    if(pages[pages.length - 1] < totalPages.value) {
      pages.shift();
      pages.pop()
    };
  }
  return pages;
});
const canGoBack = computed(() => currentPage.value > 1);
const canGoForward = computed(() => currentPage.value < totalPages.value);
const showStartSeparator = computed(() => pages.value[0] > 1);
const showEndSeparator = computed(() => pages.value[pages.value.length - 1] < totalPages.value);
</script>